/**
 * Agent Data Security Layer
 * Filters sensitive fields from agent data before exposing to clients
 */

// Fields that should NEVER be exposed publicly
const SENSITIVE_FIELDS = [
  'apiKey',
  'creatorEmail',
  'creator',
  'onboardingInstructions',
  'apiEndpoint'
]

// Fields that are safe for public consumption
const PUBLIC_FIELDS = [
  '_id',
  'name',
  'walletAddress',
  'category',
  'capabilities',
  'pricePerTask',
  'pricingModel',
  'availability',
  'responseTimeSLA',
  'supportedFormats',
  'profilePicture',
  'rating',
  'tasksCompleted',
  'totalEarnings',
  'stakeAmount',
  'role',
  'isAutonomous',
  'createdAt',
  'updatedAt',
  'bio',
  'xHandle',
  'description',
  'twitter'
]

/**
 * Sanitizes agent data by removing sensitive fields
 * @param agent - Raw agent data from database
 * @returns Sanitized agent data safe for public consumption
 */
export function sanitizeAgentData(agent: any): any {
  if (!agent) return null
  
  const sanitized: any = {}
  
  // Only copy public fields
  PUBLIC_FIELDS.forEach(field => {
    if (agent[field] !== undefined) {
      sanitized[field] = agent[field]
    }
  })
  
  return sanitized
}

/**
 * Sanitizes an array of agents
 * @param agents - Array of raw agent data
 * @returns Array of sanitized agent data
 */
export function sanitizeAgentList(agents: any[]): any[] {
  if (!Array.isArray(agents)) return []
  return agents.map(agent => sanitizeAgentData(agent))
}

/**
 * Checks if a user is the owner of an agent
 * @param agent - Agent data
 * @param userId - Current user ID
 * @returns true if user owns the agent
 */
export function isAgentOwner(agent: any, userId: string | undefined): boolean {
  if (!agent || !userId) return false
  return agent.creator === userId
}

/**
 * Gets full agent data including sensitive fields (owner only)
 * @param agent - Agent data
 * @param userId - Current user ID
 * @returns Full agent data if owner, sanitized data otherwise
 */
export function getAgentData(agent: any, userId: string | undefined): any {
  if (isAgentOwner(agent, userId)) {
    return agent // Owner can see everything
  }
  return sanitizeAgentData(agent) // Others see only public data
}
